import {
	PartWarehouseLocationEditApi,
	PartWarehouseLocationEditApi_FormInitData,
} from "src/api/generated/erp/parts/partWarehouseLocations/api/partWarehouseLocationEditApi";
import { AsyncForm, AsyncFormContentParams } from "src/components/common/forms/AsyncForm.tsx";
import { FormCommonProps } from "src/components/common/forms/types.ts";
import { PartWarehouseLocation } from "src/api/generated/erp/db/types/tables/partWarehouseLocation.ts";
import { DefaultValues } from "react-hook-form";
import {
	PartWarehouseLocationFormContent,
	PartWarehouseLocationFormValues,
} from "src/components/views/erp/parts/siteParts/partWarehouseLocations/PartWarehouseLocationFormContent.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import { dropProps } from "src/utils/dropProps.ts";

export interface PartWarehouseLocationFormProps extends FormCommonProps<number> {
	partId: number;
	partWarehouseLocationId: number | undefined;
}

export const PartWarehouseLocationForm = ({
	partId,
	partWarehouseLocationId,
	onCompleted,
	onFormEdited,
}: PartWarehouseLocationFormProps) => {
	return (
		<AsyncForm<PartWarehouseLocationEditApi_FormInitData, PartWarehouseLocationFormValues, number>
			fetch={() =>
				PartWarehouseLocationEditApi.getFormInitData({
					partId: partId,
					partWarehouseLocationId: partWarehouseLocationId,
				})
			}
			getDefaultValues={getDefaultValues}
			submit={submitForm}
			onCompleted={onCompleted}
			onFormEdited={onFormEdited}
			columns={2}
			agentContext={{
				key: defineAgentUiContextKey("erp.parts.partWarehouseLocation.form"),
				data: {
					partId,
					partWarehouseLocationId: partWarehouseLocationId ?? null,
				},
			}}
			render={({ data, ...formContentParams }) => (
				<PartWarehouseLocationFormContent
					partId={partId}
					partWarehouseLocationId={partWarehouseLocationId}
					initData={data}
					{...formContentParams}
				/>
			)}
		/>
	);

	function getDefaultValues({
		partWarehouseLocation,
		inventoryLevelEnabledByDefault,
		partHasOtherWarehouseLocations,
		siteId,
	}: PartWarehouseLocationEditApi_FormInitData): DefaultValues<PartWarehouseLocation> {
		if (partWarehouseLocation != null) return partWarehouseLocation;
		else
			return {
				partId: partId,
				locationQuantity: 1,
				defaultReplenishmentQuantity: 1,
				isDefault: !partHasOtherWarehouseLocations,
				askTransferInfo: false,
				askQuantity: false,
				autoCompleteWarehouseTransfer: false,
				inventoryLevelEnabled: inventoryLevelEnabledByDefault,
				givingSiteId: siteId,
				replenishmentMethod: "WAREHOUSE_TRANSFER",
				replenishmentTrigger: "MANUAL",
			};
	}

	async function submitForm(
		formValues: PartWarehouseLocationFormValues,
		{
			data: { partWarehouseLocation: existingPartWarehouseLocation },
		}: AsyncFormContentParams<PartWarehouseLocationEditApi_FormInitData, PartWarehouseLocationFormValues>,
	) {
		if (existingPartWarehouseLocation == null) {
			const toInsert: Omit<PartWarehouseLocation, "partWarehouseLocationId" | "lastUpdated"> = {
				...formValues,
				partId: partId,
				isActive: true,
			};
			return await PartWarehouseLocationEditApi.insert({
				partWarehouseLocation: toInsert as PartWarehouseLocation,
			});
		} else {
			const updateValues = dropProps(formValues, ["warehouseLocationId"]);
			await PartWarehouseLocationEditApi.update({
				partWarehouseLocation: {
					...updateValues,
					partWarehouseLocationId: existingPartWarehouseLocation.partWarehouseLocationId,
					lastUpdated: existingPartWarehouseLocation.lastUpdated,
					partId: existingPartWarehouseLocation.partId,
					isActive: existingPartWarehouseLocation.isActive,
				},
			});
			return existingPartWarehouseLocation.partWarehouseLocationId;
		}
	}
};
